import React, { useMemo, useState } from 'react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { appleTokens as t, tint } from '../../theme/appleTokens';
import { SearchField } from './ui/ListLayout';

/** Au-delà de ce nombre de clubs, champ de recherche dans la liste. */
const SEARCH_THRESHOLD = 6;

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function SpaceRow({ icon, label, sublabel, selected, onClick }) {
  return (
    <ButtonBase
      onClick={selected ? undefined : onClick}
      disabled={selected}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 0.875,
        textAlign: 'left',
        bgcolor: selected ? tint(t.accent, 0.12) : 'transparent',
        '&:hover': { bgcolor: selected ? tint(t.accent, 0.12) : t.surfaceHover },
        '&.Mui-disabled': { opacity: 1 },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ ...t.type.body, fontWeight: selected ? 600 : 500, color: t.label }} noWrap>
          {label}
        </Typography>
        {sublabel ? (
          <Typography sx={{ ...t.type.caption, fontWeight: 400, color: t.secondaryLabel }} noWrap>
            {sublabel}
          </Typography>
        ) : null}
      </Box>
      {selected ? <CheckIcon fontSize="small" sx={{ color: t.label }} /> : null}
    </ButtonBase>
  );
}

/**
 * Choix de l'espace DANS le menu du profil (admins de la Ligue), ERP (TopbarControls) et Ligue
 * (league/components/Layout.js) : la Ligue puis les clubs (recherche au-delà de quelques clubs, liste défilante), un
 * clic bascule. `current` : `'league'` ou le slug du club courant ; `onPick(fn)` ferme le menu puis lance `fn`.
 */
export function SpaceMenuSection({ current, clubs = [], loading = false, onSelectLeague, onSelectClub, onPick }) {
  const [query, setQuery] = useState('');
  const isLeague = current === 'league';
  const showSearch = clubs.length > SEARCH_THRESHOLD;
  const filtered = useMemo(() => {
    const q = normalizeSearch(query);
    return q ? clubs.filter((c) => normalizeSearch(c.name).includes(q) || String(c.slug).includes(q)) : clubs;
  }, [clubs, query]);
  const iconSx = { fontSize: 18, color: t.secondaryLabel, flexShrink: 0 };

  return (
    // Le menu MUI navigue au clavier sur ses enfants (et saute à la lettre tapée) : la recherche garde ses touches.
    <Box onKeyDown={(e) => e.stopPropagation()} sx={{ py: 0.75 }}>
      <Typography sx={{ ...t.type.overline, color: t.tertiaryLabel, px: 2, pb: 0.5 }}>Espace</Typography>
      <SpaceRow
        icon={<AccountBalanceOutlinedIcon sx={iconSx} />}
        label="Ligue"
        sublabel="MySquash · tous les clubs"
        selected={isLeague}
        onClick={() => onPick(onSelectLeague)}
      />
      {showSearch ? (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <SearchField value={query} onChange={setQuery} placeholder="Rechercher un club" sx={{ maxWidth: 'none', width: '100%' }} />
        </Box>
      ) : null}
      {loading && !clubs.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : filtered.length ? (
        <Box sx={{ maxHeight: 232, overflowY: 'auto' }}>
          {filtered.map((club) => (
            <SpaceRow
              key={club.slug}
              icon={<StoreOutlinedIcon sx={iconSx} />}
              label={club.name}
              selected={!isLeague && club.slug === current}
              onClick={() => onPick(() => onSelectClub?.(club))}
            />
          ))}
        </Box>
      ) : (
        <Typography sx={{ ...t.type.small, color: t.secondaryLabel, px: 2, py: 1 }}>Aucun club.</Typography>
      )}
    </Box>
  );
}
